import { useRef, useState } from 'react'
import { Button, Modal, Space, message } from 'antd'
import { CameraOutlined, RedoOutlined, SyncOutlined } from '@ant-design/icons'

interface Props {
  open: boolean
  title?: string
  onCapture: (file: File) => void
  onCancel: () => void
}

export function CameraModal({ open, title = 'Fotocamera', onCapture, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [facing, setFacing] = useState<'user' | 'environment'>('environment')
  const [frame, setFrame] = useState<string | null>(null)

  const startCamera = async (f: 'user' | 'environment' = facing) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: f, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch {
      message.error('Impossibile accedere alla fotocamera')
      onCancel()
    }
  }

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    setFrame(null)
  }

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')!
    canvasRef.current.width = videoRef.current.videoWidth
    canvasRef.current.height = videoRef.current.videoHeight
    ctx.drawImage(videoRef.current, 0, 0)
    setFrame(canvasRef.current.toDataURL('image/jpeg', 0.92))
  }

  const switchFacing = () => {
    const next: 'user' | 'environment' = facing === 'environment' ? 'user' : 'environment'
    setFacing(next)
    stopCamera()
    startCamera(next)
  }

  const confirm = async () => {
    if (!frame) return
    const blob = await fetch(frame).then(r => r.blob())
    const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' })
    stopCamera()
    onCapture(file)
  }

  const handleCancel = () => {
    stopCamera()
    onCancel()
  }

  return (
    <Modal
      open={open}
      title={title}
      footer={null}
      onCancel={handleCancel}
      afterOpenChange={isOpen => { if (isOpen) startCamera(); else stopCamera() }}
      width={520}
      centered
    >
      {!frame ? (
        <div>
          <video ref={videoRef} autoPlay playsInline
            style={{ width: '100%', borderRadius: 6, background: '#000', maxHeight: 340, objectFit: 'cover' }} />
          <Space style={{ marginTop: 10 }}>
            <Button type="primary" size="large" icon={<CameraOutlined />} onClick={snap}
              style={{ minWidth: 100, minHeight: 48 }}>
              Scatta
            </Button>
            <Button icon={<SyncOutlined />} onClick={switchFacing} style={{ minHeight: 48 }}>
              Inverti
            </Button>
          </Space>
        </div>
      ) : (
        <div>
          <img src={frame} alt="anteprima" style={{ width: '100%', borderRadius: 6 }} />
          <Space style={{ marginTop: 10 }}>
            <Button type="primary" size="large" icon={<CameraOutlined />} onClick={confirm}
              style={{ minWidth: 140, minHeight: 48 }}>
              Usa questa foto
            </Button>
            <Button icon={<RedoOutlined />} onClick={() => setFrame(null)} style={{ minHeight: 48 }}>
              Riprova
            </Button>
          </Space>
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Modal>
  )
}
